// ─────────────────────────────────────────────────────────────────────────────
// Cliente Supabase — um só para o app inteiro.
//
// Na versão 1.x cada trecho do renderer chamava getSupabaseClient() e guardava
// a instância numa variável solta. Aqui o cliente é criado uma vez, na carga do
// módulo, e todo mundo importa o mesmo.
//
// O schema padrão é "orion" (não "public"): é onde vivem orion_errors,
// operador, operador_atividade, ocorrencia, criticidade, user_emp e as demais
// tabelas do app.
// ─────────────────────────────────────────────────────────────────────────────

import { createClient } from '@supabase/supabase-js';
import { SUPABASE_URL, SUPABASE_KEY, ORION_SCHEMA, verificarChaveSupabase } from '../config/ambiente';

// Confere a configuração e grita no console se algo estiver errado — chave
// ausente, ou uma service_role no lugar de uma anon (ver config/ambiente.js).
verificarChaveSupabase();

/** true quando há Supabase configurado. As telas usam para explicar a falha. */
export let supabaseConfigurado = !!(SUPABASE_URL && SUPABASE_KEY);

// `createClient` lança se a URL vier vazia, e isso derrubaria o app inteiro na
// carga do módulo — antes de qualquer tela conseguir explicar o que faltou.
// Sem configuração, o cliente vira um objeto que recusa toda chamada com uma
// mensagem clara; o app abre, e o que depende do banco avisa o motivo.
const MSG_SEM_CONFIG = 'Supabase não configurado. Preencha REACT_APP_SUPABASE_URL e '
  + 'REACT_APP_SUPABASE_ANON_KEY no arquivo .env.';

function clienteAusente() {
  const recusa = () => Promise.reject(new Error(MSG_SEM_CONFIG));

  // Imita o encadeamento do supabase-js: `.select().eq().order()` devolve
  // sempre o mesmo objeto. O `then` é o que importa — é ele que faz o `await`
  // rejeitar com a mensagem, em vez de ficar pendurado para sempre.
  const builder = new Proxy({}, {
    get(_alvo, prop) {
      if (prop === 'then') return (_resolve, rejeitar) => rejeitar(new Error(MSG_SEM_CONFIG));
      if (prop === 'catch') return (aoFalhar) => Promise.resolve(aoFalhar(new Error(MSG_SEM_CONFIG)));
      return () => builder;
    },
  });

  return {
    from: () => builder,
    schema: () => ({ from: () => builder, rpc: recusa }),
    rpc: recusa,
    channel: () => ({ on() { return this; }, subscribe() { return this; } }),
    removeChannel: () => {},
    storage: {
      from: () => ({ upload: recusa, getPublicUrl: () => ({ data: { publicUrl: '' } }) }),
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// A CHAVE PODE CHEGAR DEPOIS
//
// Até 22/08/2026 o cliente era criado uma vez, na carga do módulo, com a chave
// embutida no build. Agora ela pode vir do login (o n8n a devolve junto com o
// nome do operador) — e nesse caso ela só existe DEPOIS de o módulo carregar.
//
// Trocar os seis módulos que importam `supabase` seria arriscado e espalharia o
// problema. Em vez disso, `supabase` é um intermediário que sempre aponta para o
// cliente vigente. Quem importa não muda uma linha e nem precisa saber disso.
//
// `bind` é obrigatório: sem ele, `supabase.from(...)` chamaria o método solto do
// cliente, sem o `this`, e o supabase-js quebraria por dentro.
// ─────────────────────────────────────────────────────────────────────────────

function criarCliente(url, chave) {
  return createClient(url, chave, { db: { schema: ORION_SCHEMA } });
}

// Chave inválida (service_role) ainda cria o cliente — ela funciona, só é
// insegura, e o aviso já saiu no console. O cliente só deixa de existir quando
// não há configuração NENHUMA.
let clienteAtual = supabaseConfigurado
  ? criarCliente(SUPABASE_URL, SUPABASE_KEY)
  : clienteAusente();

// Cliente do schema dos chamados de T.I. Vive aqui, e não em chamadoTiApi, para
// que a troca de chave alcance os dois de uma vez — esquecer este deixaria a
// aba de T.I. falando com o banco pela chave antiga.
let clienteTiAtual = supabaseConfigurado
  ? createClient(SUPABASE_URL, SUPABASE_KEY, { db: { schema: 'chamados_ti' } })
  : clienteAusente();

/** Credenciais em uso, para quem precisa saber se já há uma chave válida. */
let credenciais = {
  url: SUPABASE_URL || '',
  chave: SUPABASE_KEY || '',
  origem: supabaseConfigurado ? 'build' : 'nenhuma',
};

function intermediario(obter) {
  return new Proxy({}, {
    get(_alvo, prop) {
      const alvo = obter();
      const valor = alvo[prop];
      return typeof valor === 'function' ? valor.bind(alvo) : valor;
    },
  });
}

export const supabase = intermediario(() => clienteAtual);
export const supabaseTi = intermediario(() => clienteTiAtual);

/**
 * Aponta o Orion para outra chave — normalmente a que veio do login.
 *
 * Devolve true quando trocou. Chamar com a mesma chave que já está em uso não
 * recria nada: recriar o cliente derruba as inscrições de Realtime abertas, e a
 * aba Incidentes perderia a atualização automática sem motivo.
 */
export function configurarSupabase({ url, chave, origem = 'login' } = {}) {
  const novaUrl = String(url || '').trim();
  const novaChave = String(chave || '').trim();
  if (!novaUrl || !novaChave) return false;
  if (novaUrl === credenciais.url && novaChave === credenciais.chave) return false;

  try {
    clienteAtual = criarCliente(novaUrl, novaChave);
    clienteTiAtual = createClient(novaUrl, novaChave, { db: { schema: 'chamados_ti' } });
    credenciais = { url: novaUrl, chave: novaChave, origem };
    supabaseConfigurado = true;
    // Projeto novo pode não ter a mesma estrutura: a fonte de operadores volta
    // a ser descoberta na próxima leitura.
    fonteOperadorEmUso = null;
    return true;
  } catch (err) {
    // Chave malformada não pode derrubar o login: o que já estava em uso segue
    // valendo, e o operador entra com o comportamento anterior.
    console.error('[Orion] Não consegui usar a chave recebida no login:', err.message);
    return false;
  }
}

/** true quando há Supabase utilizável (do build ou do login). */
export function temSupabase() {
  return !!(credenciais.url && credenciais.chave);
}

/** De onde veio a chave em uso: 'build', 'login', 'sessao' ou 'nenhuma'. */
export function origemDaChave() {
  return credenciais.origem;
}

/**
 * As credenciais em uso, para quem precisa persisti-las (ver OrionContext).
 * Devolve cópia: quem chama não deve conseguir alterar o que está valendo sem
 * passar por configurarSupabase.
 */
export function credenciaisEmUso() {
  return { url: credenciais.url, chave: credenciais.chave };
}

/**
 * De onde o app LÊ os operadores.
 *
 * É a view `orion.vw_operador`, e não a tabela: a identidade do operador
 * (nome, e-mail, setor, ativo) mora em `space.usuarios`, e a view é quem junta
 * as duas — ver sql/view-operador_orion-fonte-space_*.sql.
 *
 * A view expõe as MESMAS colunas `ope_*` da tabela, de propósito: nenhuma
 * consulta precisou ser reescrita, e voltar atrás é trocar esta constante por
 * `'operador'`.
 *
 * Vale só para leitura. Escrita em operador não existe no app hoje, e se um
 * dia existir tem de ir para a tabela — view com join não aceita UPDATE.
 */
export const FONTE_OPERADOR = 'vw_operador';

/** A tabela crua, para quando a view não existe. */
const FONTE_OPERADOR_ANTIGA = 'operador';

// Qual das duas está valendo nesta sessão. `null` = ainda não se sabe.
let fonteOperadorEmUso = null;
let descobrindoFonte = null;

/**
 * De onde ler operadores AGORA — a view, se ela existir; a tabela, se não.
 *
 * Existe por causa da ordem de implantação. A view é criada por um script
 * rodado à mão no Supabase (sql/view-operador_orion-fonte-space_*.sql), e o app
 * é publicado por outro caminho: não há como garantir qual vem primeiro. Sem
 * esta verificação, subir o app antes do script deixaria TODAS as leituras de
 * operador falhando — sem código, sem setor, sem jornada, ninguém entra.
 *
 * A descoberta acontece uma vez por sessão e fica guardada. `configurarSupabase`
 * a esquece, porque trocar de projeto pode trocar o que existe lá dentro.
 *
 * Quando a view não é encontrada, o app volta a ler a tabela e continua
 * funcionando como antes — só sem enxergar o setor vindo do space.
 */
export async function fonteOperador() {
  if (fonteOperadorEmUso) return fonteOperadorEmUso;

  if (!descobrindoFonte) {
    descobrindoFonte = (async () => {
      try {
        // `head` = sem corpo; `limit(1)` = sem varrer a tabela. A pergunta é só
        // "esta relação existe e eu posso lê-la?".
        const { error } = await supabase
          .schema(ORION_SCHEMA)
          .from(FONTE_OPERADOR)
          .select('ope_codigo', { head: true, count: 'exact' })
          .limit(1);

        if (error) throw new Error(error.message);
        fonteOperadorEmUso = FONTE_OPERADOR;
      } catch (e) {
        console.warn(
          `[Orion] A view orion.${FONTE_OPERADOR} não respondeu (${e.message}). `
          + `Lendo de orion.${FONTE_OPERADOR_ANTIGA} — o setor virá do Orion, e não do space. `
          + 'Rode sql/view-operador_orion-fonte-space_*.sql para ligar a fonte nova.'
        );
        fonteOperadorEmUso = FONTE_OPERADOR_ANTIGA;
      }
      return fonteOperadorEmUso;
    })().finally(() => { descobrindoFonte = null; });
  }

  return descobrindoFonte;
}

/** Esquece a fonte descoberta. Chamado ao trocar de projeto do Supabase. */
export function esquecerFonteOperador() {
  fonteOperadorEmUso = null;
}

/** Query builder já apontado para uma tabela do schema orion. */
export function tabela(nome) {
  return supabase.schema(ORION_SCHEMA).from(nome);
}

/** Query builder da fonte de operadores em uso (a view, ou a tabela). */
export async function tabelaOperador() {
  return tabela(await fonteOperador());
}

/** Query builder para uma tabela de OUTRO schema (ex.: 'sistemas', 'public'). */
export function tabelaDoSchema(schema, nome) {
  return supabase.schema(schema).from(nome);
}
